import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { objectGetValues } from '../../utils/ieCompatibility'
import styles from '../../styles'
import SvgSearch from '../../icons/SvgSearch'
import { WHITE } from '../../theme'
import { Form } from 'react-final-form'
import arrayMutators from 'final-form-arrays'
import Button from '../button/Button'
import { makeStyles } from 'tss-react/mui'
import { ClickAwayListener, Grid } from '@mui/material'
import { champsMenuRechercheEnum, rechercheAvanceeEnum, surfaceEnum } from '../../modules/resultats-recherche/enums/champsMenuRecherche'
import { RechercheContext } from '../../modules/resultats-recherche/utils/RechercheContext'
import { getResultatRecherche } from '../../modules/resultats-recherche/api/getResultatRecherche'
import { FormulaireRechercheModel, INITIAL_FORMULAIRE_RECHERCHE } from '../../modules/resultats-recherche/types/formulaireRechercheModel'
import { ResponsiveContext } from '../../utils/context/ResponsiveContext'
import { FORM_ID } from '../../utils/globalConstant'
import linksEnum from '../../enums/linksEnum'
import { useLocation, useNavigate } from 'react-router-dom'
import PopinMenuRecherche from './components/PopinMenuRecherche'
import { URL_PARAMS_FILTRES } from '../../modules/resultats-recherche/utils/rechercheConstants'
import ChampSelect from './components/ChampSelect'

const useStyles = makeStyles<{ isMenuLargeScreen: boolean }>()(
	(theme, { isMenuLargeScreen }) => ({
		menuRechercheContainer: {
			height: 75,
			border: '1px solid #D9D9D9',
			borderRadius: 42,
			padding: isMenuLargeScreen ? '0px 10px 0px 7px' : '0px 7.5px 0px 7px',
			marginTop: isMenuLargeScreen ? 0 : 11
		},
		searchIcon: {
			height: 'fit-content',
			textTransform: 'inherit',
			padding: 20,
			minWidth: 60,
			borderRadius: 32,
			width: 'fit-content',
			cursor: 'pointer'
		},
		errors: {
			border: '1px solid #B92D00',
			borderRadius: 38,
			'& .champ-label': {
				color: '#B92D00 !important'
			}
		},
		menuStandalone: {
			backgroundColor: WHITE,
			height: 'fit-content !important',
			width: '100%',
			display: 'flex',
			justifyContent: 'center',
			borderRadius: '0 0 30px 30px',
			padding: isMenuLargeScreen ? '11px 60px' : '11px 30px',
			boxSizing: 'border-box'
		},
		modifierRecherche: {
			paddingLeft: 31,
			...styles(theme).text8,
			cursor: 'pointer'
		}
	})
)

interface AcceptingProps {
	standalone?: boolean
}

type MenuRechercheProps = AcceptingProps

const MenuRecherche: React.FC<MenuRechercheProps> = (
	{
		standalone
	}
) => {
	const { isMenuLargeScreen } = useContext(ResponsiveContext)
	const { classes, cx } = useStyles({ isMenuLargeScreen })
	const location = useLocation()
	const navigate = useNavigate()

	const [formulaireOuvert, setFormulaireOuvert] = useState('')
	const { setResultat, setLoading, filtres, setFiltres, champTouche, setChampTouche } = useContext(RechercheContext)
	const [formulaireTouche, setFormulaireTouche] = useState(false)
	const [rechercheEffectuee, setRechercheEffectuee] = useState(false)
	const [popinRecherche, setPopinRecherche] = useState(false)

	const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])

	const initialValue = useMemo(() =>
			!searchParams.get(URL_PARAMS_FILTRES) ?
				INITIAL_FORMULAIRE_RECHERCHE
				:
				// Si un champ a été touché (et donc potentiellement modifié), on réutilise les valeurs du form
				champTouche ? {
						...filtres, tri: JSON.parse(searchParams.get(URL_PARAMS_FILTRES) as string).tri
					} :
					{
						...JSON.parse(searchParams.get(URL_PARAMS_FILTRES) as string),
						[champsMenuRechercheEnum.rechercheAvancee]: {
							...INITIAL_FORMULAIRE_RECHERCHE[champsMenuRechercheEnum.rechercheAvancee],
							...JSON.parse(searchParams.get(URL_PARAMS_FILTRES) as string)[champsMenuRechercheEnum.rechercheAvancee]
						}
					}
		// On ne veut pas mettre en dépendance filtre car c'est update trop souvent dans le formulaire (voir render du Form)
		// eslint-disable-next-line react-hooks/exhaustive-deps
		, [searchParams, location.search])

	const onSubmit = useCallback((values: FormulaireRechercheModel) => {
		setRechercheEffectuee(false)
		const surfaceMin = values[champsMenuRechercheEnum.rechercheAvancee] && values[champsMenuRechercheEnum.rechercheAvancee][rechercheAvanceeEnum.surface] ? values[champsMenuRechercheEnum.rechercheAvancee][rechercheAvanceeEnum.surface][surfaceEnum.min] : null
		const surfaceMax = values[champsMenuRechercheEnum.rechercheAvancee] && values[champsMenuRechercheEnum.rechercheAvancee][rechercheAvanceeEnum.surface] ? values[champsMenuRechercheEnum.rechercheAvancee][rechercheAvanceeEnum.surface][surfaceEnum.max] : null

		// Si la surface min saisie est > à la valeur de surface max, on vide le champ max
		if (surfaceMin && surfaceMax && surfaceMin > surfaceMax) {
			values[champsMenuRechercheEnum.rechercheAvancee][rechercheAvanceeEnum.surface][surfaceEnum.max] = null
		}

		const budgetMin = values[champsMenuRechercheEnum.budget] ? values[champsMenuRechercheEnum.budget][surfaceEnum.min] : null
		const budgetMax = values[champsMenuRechercheEnum.budget] ? values[champsMenuRechercheEnum.budget][surfaceEnum.max] : null

		if (budgetMin && budgetMax && budgetMin > budgetMax) {
			values[champsMenuRechercheEnum.budget][surfaceEnum.min] = null
		}

		const filtreUrlAPush = !searchParams.get(URL_PARAMS_FILTRES) ? values : { ...values, tri: JSON.parse(searchParams.get(URL_PARAMS_FILTRES) as string).tri }
		navigate(`${linksEnum.RECHERCHE_OFFRE}?filtres=${JSON.stringify(filtreUrlAPush)}`)
	}, [navigate, searchParams])

	useEffect(() => {
		// Si les paramètres de l'URL change, on doit refaire une recherche
		setRechercheEffectuee(false)
		setChampTouche(false)
	}, [location.search, setChampTouche])

	useEffect(() => {
		if (!rechercheEffectuee && !!searchParams.get(URL_PARAMS_FILTRES)) {
			setLoading(true)
			const filterValues = {
				...JSON.parse(searchParams.get(URL_PARAMS_FILTRES) as string),
				[champsMenuRechercheEnum.rechercheAvancee]: {
					...INITIAL_FORMULAIRE_RECHERCHE[champsMenuRechercheEnum.rechercheAvancee],
					...JSON.parse(searchParams.get(URL_PARAMS_FILTRES) as string)[champsMenuRechercheEnum.rechercheAvancee]
				},
				tri: initialValue.tri
			}

			getResultatRecherche(filterValues)
				.then((resultat: any) => {
					setFiltres(filterValues)
					setResultat(resultat)
					setLoading(false)
					setRechercheEffectuee(true)
				})
		}
	}, [searchParams, rechercheEffectuee, initialValue, setFiltres, setLoading, setResultat])

	return (
		<Form
			mutators={{ ...arrayMutators }}
			initialValues={initialValue}

			onSubmit={onSubmit}
			render={({ handleSubmit, errors, form, values }) => {
				setFiltres(values)
				return (
					<form
						id={FORM_ID}
						defaultValue={initialValue}
						onSubmit={handleSubmit}
						className={cx({
							[classes.menuStandalone]: standalone
						})}
					>
						<Grid container item xs={12} alignItems="center" className={classes.menuRechercheContainer}>
							{isMenuLargeScreen ? (
								// Bloc de champs de formulaire
								<ClickAwayListener onClickAway={() => setFormulaireOuvert('')}>
									<Grid container item xs={11}>
										{objectGetValues(champsMenuRechercheEnum).map((champMenuRecherche, index: number) => (
											<Grid container item xs={3} key={champMenuRecherche}
											      className={cx({
												      [classes.errors]: !!errors && champMenuRecherche in errors && formulaireTouche
											      })}
											>
												<ChampSelect
													formValues={values}
													typeChamps={champMenuRecherche}
													index={index}
													formulaireOuvert={formulaireOuvert}
													setFormulaireOuvert={setFormulaireOuvert}
												/>
											</Grid>
										))}
									</Grid>
								</ClickAwayListener>
							) : (
								<Grid container item xs={11} className={classes.modifierRecherche} onClick={() => setPopinRecherche(true)}>
									<FormattedMessage
										id="resultatsRecherche.menu.modifierRecherche"
										defaultMessage="Modifier ma recherche"
										description="Texte de modification de la recherche"
									/>

									<PopinMenuRecherche
										popinRecherche={popinRecherche}
										onCloseDialog={() => setPopinRecherche(false)}
										formValues={values}
										formulaireTouche={formulaireTouche}
										setFormulaireTouche={setFormulaireTouche}
										form={form}
										errors={errors}
									/>
								</Grid>
							)}

							{/* Bloc pour l'image de recherche */}
							<Grid container item xs={1} justifyContent="flex-end">
								<Button
									buttonStyle={classes.searchIcon}
									background="backgroundAccessibility"
									color={WHITE}
									textStyle="text4"
									type={isMenuLargeScreen ? 'submit' : 'button'}
									onClick={() => {
										if (isMenuLargeScreen) {
											setFormulaireOuvert('')
											setFormulaireTouche(true)
										} else {
											setPopinRecherche(true)
										}
									}}
								>
									<SvgSearch color={WHITE} />
								</Button>
							</Grid>
						</Grid>
					</form>
				)
			}}
		/>
	)
}

export default MenuRecherche